import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Monitor, Activity, AlertCircle, CheckCircle2, Clock, Server, Database, Cpu, HardDrive, Network, MemoryStick, Zap, TrendingUp, TrendingDown, RefreshCw, Settings, Bell, Eye, EyeOff, Filter, Search, Download, Upload, BarChart3, PieChart, LineChart, Target, Shield, Users, Globe, Mail, Phone, MessageSquare, FileText, Calendar, Timer, Brain, Route, Lightning, MapPin, Home, Menu, X, Check, XCircle, Info, HelpCircle, ExternalLink, Copy, Lock, Unlock, Key, Plus, Edit, Trash2, Save, Play, Pause, Stop, RotateCcw, Archive, Trash, Star, Heart, Award, Trophy, Medal, Gift, Coffee, Pizza, Cake, Music, Camera, Video, Share, Bookmark, Flag, Bell as BellIcon, History, Crown, Zap as ZapIcon, Target as TargetIcon, Timer as TimerIcon, Brain as BrainIcon, Route as RouteIcon, Lightning as LightningIcon, MapPin as MapPinIcon
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const SystemMonitoring = () => {
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [healthChecks, setHealthChecks] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [monitoringConfig, setMonitoringConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('1h');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadSystemData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const metricsResponse = await AdminService.getSystemMetrics(timeRange);
      const healthResponse = await AdminService.getHealthChecks();
      const performanceResponse = await AdminService.getPerformanceData(timeRange);
      const alertsResponse = await AdminService.getSystemAlerts();
      const configResponse = await AdminService.getMonitoringConfig();

      if (metricsResponse.success && healthResponse.success && performanceResponse.success && 
          alertsResponse.success && configResponse.success) {
        setSystemMetrics(metricsResponse.data);
        setHealthChecks(healthResponse.data);
        setPerformanceData(performanceResponse.data);
        setAlerts(alertsResponse.data);
        setMonitoringConfig(configResponse.data);
      } else {
        setError(metricsResponse.message || healthResponse.message || performanceResponse.message || 
                 alertsResponse.message || configResponse.message);
      }
    } catch (err) {
      setError('Failed to fetch system monitoring data.');
      console.error('Error fetching system data:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadSystemData();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(loadSystemData, 30000); // Refresh every 30 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loadSystemData, autoRefresh]);

  const handleConfigureMonitoring = async (configData) => {
    if (window.confirm('Are you sure you want to update monitoring configuration?')) {
      try {
        const response = await AdminService.updateMonitoringConfig(configData);
        if (response.success) {
          alert('Monitoring configuration updated successfully!');
          loadSystemData();
        } else {
          alert(`Failed to update configuration: ${response.message}`);
        }
      } catch (err) {
        alert('Error updating monitoring configuration.');
        console.error('Error updating configuration:', err);
      }
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    if (window.confirm('Are you sure you want to acknowledge this alert?')) {
      try {
        const response = await AdminService.acknowledgeAlert(alertId);
        if (response.success) {
          alert('Alert acknowledged successfully!');
          loadSystemData();
        } else {
          alert(`Failed to acknowledge alert: ${response.message}`);
        }
      } catch (err) {
        alert('Error acknowledging alert.');
        console.error('Error acknowledging alert:', err);
      }
    }
  };

  const handleResolveAlert = async (alertId) => {
    if (window.confirm('Are you sure you want to resolve this alert?')) {
      try {
        const response = await AdminService.resolveAlert(alertId);
        if (response.success) {
          alert('Alert resolved successfully!');
          loadSystemData();
        } else {
          alert(`Failed to resolve alert: ${response.message}`);
        }
      } catch (err) {
        alert('Error resolving alert.');
        console.error('Error resolving alert:', err);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      case 'unknown': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-blue-600 bg-blue-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading system monitoring data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  if (!systemMetrics) {
    return <div className="p-6 text-center text-gray-500">No system monitoring data available.</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Monitor className="mr-3 text-blue-600" size={30} />
          System Monitoring
        </h1>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Auto Refresh:</label>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-1 rounded-md text-sm font-medium ${
                autoRefresh ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {autoRefresh ? 'ON' : 'OFF'}
            </button>
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="15m">Last 15 Minutes</option>
            <option value="1h">Last Hour</option>
            <option value="6h">Last 6 Hours</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <button
            onClick={loadSystemData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="mr-2" size={18} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-8">
        Monitor system health, performance metrics, and alerts in real-time to ensure optimal system operation.
        <span className="ml-2 text-sm text-gray-500">Last Updated: {new Date(systemMetrics.lastUpdated).toLocaleString()}</span>
      </p>

      {/* System Overview Metrics */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <LayoutDashboard className="mr-2 text-purple-600" size={24} /> System Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {systemMetrics.overviewMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(metric.status)}`}>
                    {metric.status}
                  </span>
                  <span className="ml-3 text-gray-500">{metric.description}</span>
                </div>
              </div>
              {metric.type === 'cpu_usage' && <Cpu size={40} className="text-blue-500 opacity-70" />}
              {metric.type === 'memory_usage' && <MemoryStick size={40} className="text-green-500 opacity-70" />}
              {metric.type === 'disk_usage' && <HardDrive size={40} className="text-orange-500 opacity-70" />}
              {metric.type === 'network_usage' && <Network size={40} className="text-purple-500 opacity-70" />}
            </div>
          ))}
        </div>
      </section>

      {/* System Health Checks */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <CheckCircle2 className="mr-2 text-green-600" size={24} /> Health Checks
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">System Health Status</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Last Check:</span>
              <span className="text-sm font-medium text-gray-900">
                {new Date(systemMetrics.lastHealthCheck).toLocaleString()}
              </span>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Check
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {healthChecks.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No health checks available.</td>
                </tr>
              ) : (
                healthChecks.map((check) => (
                  <tr key={check.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{check.service}</div>
                      <div className="text-sm text-gray-500">{check.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(check.status)}`}>
                        {check.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{check.responseTime}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(check.lastCheck).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{check.details}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Performance Monitoring */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Activity className="mr-2 text-orange-600" size={24} /> Performance Monitoring
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Cpu className="mr-2 text-blue-600" size={20} /> CPU Performance
            </h3>
            <div className="space-y-4">
              {performanceData.cpuMetrics.map((metric, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{metric.name}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${metric.value}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{metric.value}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <MemoryStick className="mr-2 text-green-600" size={20} /> Memory Performance
            </h3>
            <div className="space-y-4">
              {performanceData.memoryMetrics.map((metric, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{metric.name}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${metric.value}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{metric.value}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* System Alerts */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <AlertCircle className="mr-2 text-red-600" size={24} /> System Alerts
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Active Alerts</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Total:</span>
              <span className="text-sm font-medium text-gray-900">{alerts.length}</span>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alert
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Triggered
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {alerts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No active alerts.</td>
                </tr>
              ) : (
                alerts.map((alert) => (
                  <tr key={alert.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{alert.title}</div>
                      <div className="text-sm text-gray-500">{alert.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityColor(alert.severity)}`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(alert.status)}`}>
                        {alert.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(alert.triggeredAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {alert.status === 'active' && (
                        <>
                          <button
                            onClick={() => handleAcknowledgeAlert(alert.id)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="Acknowledge Alert"
                          >
                            <Eye className="inline" size={16} />
                          </button>
                          <button
                            onClick={() => handleResolveAlert(alert.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Resolve Alert"
                          >
                            <CheckCircle2 className="inline" size={16} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Monitoring Configuration */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <Settings className="mr-2 text-gray-600" size={24} /> Monitoring Configuration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <Bell className="mr-2 text-yellow-600" size={20} /> Alert Settings
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">CPU Threshold</span>
                <span className="text-sm font-medium text-gray-900">{monitoringConfig.cpuThreshold}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Memory Threshold</span>
                <span className="text-sm font-medium text-gray-900">{monitoringConfig.memoryThreshold}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Disk Threshold</span>
                <span className="text-sm font-medium text-gray-900">{monitoringConfig.diskThreshold}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Check Interval</span>
                <span className="text-sm font-medium text-gray-900">{monitoringConfig.checkInterval}s</span>
              </div>
            </div>
            <button
              onClick={() => handleConfigureMonitoring({})}
              className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Settings className="mr-2" size={18} /> Configure Alerts
            </button>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <Activity className="mr-2 text-green-600" size={20} /> Performance Settings
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Data Retention</span>
                <span className="text-sm font-medium text-gray-900">{monitoringConfig.dataRetention} days</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Sampling Rate</span>
                <span className="text-sm font-medium text-gray-900">{monitoringConfig.samplingRate}s</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Log Level</span>
                <span className="text-sm font-medium text-gray-900">{monitoringConfig.logLevel}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Auto Cleanup</span>
                <span className="text-sm font-medium text-gray-900">{monitoringConfig.autoCleanup ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
            <button
              onClick={() => handleConfigureMonitoring({})}
              className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <Settings className="mr-2" size={18} /> Configure Performance
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SystemMonitoring;